import { BaseAdapter } from './base';
import { type EnvironmentInfo, type PlatformCapabilities, PlatformEnum } from '../utils';
import { EnvironmentDetector, type AsyncStorage } from '../core';

/**
 * Web 平台适配器
 * 直接映射 window 和 document 对象到统一的 Platform 接口
 */
export class WebAdapter extends BaseAdapter {
  readonly platformType: PlatformEnum = PlatformEnum.Web;
  readonly env: EnvironmentInfo;
  readonly capabilities: PlatformCapabilities;

  constructor() {
    super();
    this.env = EnvironmentDetector.getEnvironmentInfo();
    this.capabilities = EnvironmentDetector.detectCapabilities(this.platformType);
  }

  protected getDefaultLocalStorage(): AsyncStorage {
    const nativeStorage = window.localStorage;
    return {
      get length() {
        return nativeStorage.length;
      },
      key: function(index: number): string | null {
        return nativeStorage.key(index);
      },
      clear: function(): void {
        nativeStorage.clear();
      },
      /** 特别小心，这里是异步读取！！为了和 LiteApp 保持一致 */
      getItem: async function(key: string): Promise<string | null> {
        return nativeStorage.getItem(key);
      },
      removeItem: function(key: string): void {
        nativeStorage.removeItem(key);
      },
      setItem: function(key: string, value: string): void {
        nativeStorage.setItem(key, value);
      },
    };
  }
  /**
   * 获取Cookie
   * @param {string} name - 要获取的Cookie名称
   * @returns {string} Cookie值或空字符串（如果不存在）
   */
  protected getDefaultCookie(name: string): string {
    const cookies = document.cookie;
    const regex = new RegExp(`(^| )${name}=([^;]+)`);
    const match = cookies.match(regex);
    return match?.[2] ? decodeURIComponent(match[2]) : '';
  }
  /**
   * 设置Cookie
   * @param {string} name - Cookie名称
   * @param {string} value - Cookie值
   * @param {number} days - 过期天数（默认7天）
   * @param {string} path - 有效路径（默认'/'）
   * @param {string} domain - 有效域名（默认当前域名）
   * @param {boolean} secure - 是否仅HTTPS传输（默认false）
   */
  protected setDefaultCookie(options: { name: string; value: string; days?: number | undefined; path?: string | undefined; domain?: string | undefined; secure?: boolean | undefined; }): void {
    const { name, value, days = 7, path = '/', domain = '', secure = false } = options;
    if (!name || !value) {
      console.error('web setCookie name and value are required');
      return;
    }
    let cookieString = `${name}=${encodeURIComponent(value)}`;
    // 设置过期时间
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      cookieString += `; expires=${date.toUTCString()}`;
    }
    // 设置路径
    cookieString += `; path=${path}`;
    // 设置域名
    if (domain) {
      cookieString += `; domain=${domain}`;
    }
    // 设置Secure标志
    if (secure) {
      cookieString += '; Secure';
    }
    document.cookie = cookieString;
  }

  // protected doDefaultFetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
  //   return window.fetch(input, init);
  // }

  protected getDefaultNavigator(): Navigator {
    return window.navigator;
  }

  protected getDefaultLocation(): Location {
    return window.location;
  }

  protected getDefaultDocument(): Document {
    return document;
  }

  /** 获取全局数据配置（只读） */
  public getGlobalDataConfig(): Record<string, unknown> {
    return {};
  }
}
